import * as React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { useParams } from 'react-router-dom';
import { Box, Grid, Typography } from '@mui/material';
import { useFormik } from 'formik';
import dayjs from 'dayjs';
import { number, object, date, string } from 'yup';
import { type OtherTestFormValues } from './types';
import { otherTestTypeOptions } from './options';
import {
  editOtherTestPermission,
  type OtherTestFieldPermissions,
} from './editOtherTestPermission';
import { PAGE_TEXT } from '../lang';
import { APPLICATION_MODULE, OTHER_TEST_TYPE } from '../constants';
import { APPLICATION_STATUS_MODULE } from '../../ApplicationStatus/constants';
import {
  type OtherTestFormData,
  addApplicationItem,
  updateApplicationItem,
} from '../../../graphql';
import {
  type AlertDetails,
  Alert,
  Button,
  TextField,
  Select,
  SwitchButton,
  DatePicker,
  Dialog,
  FilesPanel,
  type FileData,
  FILE_MANAGE_ORDER,
} from '../../../../../components';
import {
  handleReadySubmit,
  handleFileRename,
  type FormController,
} from '../../../../../utils';

export type OtherTestFormProps = {
  applicationNo: string;
  initialValues?: OtherTestFormData;
  enableEdit: FormController;
  isConditionEditable: boolean;
  enableDelete: boolean;
  refetchApplication: () => void;
  handleDelete: () => void;
  handleLoading: (callback: () => Promise<void>) => Promise<void>;
};

type ResultFields = {
  label: string;
  display: boolean;
  children: Array<{
    name: keyof OtherTestFormValues;
    label: string;
    min: number;
    max: number;
  }>;
};

const {
  QUALIFICATION: { OTHER_TEST },
  BUTTON,
} = PAGE_TEXT;

export const OtherTestForm: React.FunctionComponent<
  OtherTestFormProps
> = props => {
  const {
    applicationNo,
    enableDelete,
    initialValues = {
      testType: '',
      testDate: null,
      resultAnnouncedIndicator: false,
      expectedResultUpdateDate: null,
      totalScore: '',
      totalPercentile: '',
      quantitativeScore: '',
      quantitativePercentile: '',
      analyticalWritingScore: '',
      analyticalWritingPercentile: '',
      verbalScore: '',
      verbalPercentile: '',
      integratedReasoningScore: '',
      integratedReasoningPercentile: '',
      dataInsightsScore: '',
      dataInsightsPercentile: '',
      supportingDocuments: [],
      referenceNo: '',
    },
    isConditionEditable,
    enableEdit,
    refetchApplication,
    handleDelete,
    handleLoading,
  } = props;

  const {
    supportingDocuments = [],
    otherTestId: initialTestId,
    ...others
  } = initialValues;

  const [openDialog, setOpenDialog] = useState(false);
  const [files, setFiles] = useState<FileData[]>(supportingDocuments);
  const [otherTestId, setOtherTestId] = useState(initialTestId ?? '');

  let alertDetailDef: AlertDetails = {
    alertContent: '',
    alertType: 'info',
  };
  const [openAlert, setOpenAlert] = useState(false);
  const [alertDetails, setAlertDetails] =
    useState<AlertDetails>(alertDetailDef);

  const { applicationId = '' } = useParams();

  const createOtherTest = addApplicationItem('otherTests', refetchApplication);
  const updateOtherTest = updateApplicationItem();
  const handleSubmit = async (values: OtherTestFormValues): Promise<void> => {
    if (files.length === 0 && values.resultAnnouncedIndicator) {
      setAlertDetails({
        alertContent: 'Please upload relevant supporting documents.',
        alertType: 'warning',
      });
      setOpenAlert(true);
      return;
    }

    const inputSupportingDocuments: FileData[] = files.map((doc, index) => {
      const { name, ...other } = doc;
      return {
        name: handleFileRename(applicationNo, 'other_test', index + 1),
        ...other,
      };
    });

    const {
      totalScore,
      totalPercentile,
      quantitativeScore,
      quantitativePercentile,
      analyticalWritingScore,
      analyticalWritingPercentile,
      verbalScore,
      verbalPercentile,
      integratedReasoningScore,
      integratedReasoningPercentile,
      expectedResultUpdateDate,
      dataInsightsScore,
      dataInsightsPercentile,
      resultReceiptDate,
      ...others
    } = values;

    const otherTest = {
      result: {
        totalScore: typeof totalScore === 'number' ? totalScore : null,
        totalPercentile:
          typeof totalPercentile === 'number' ? totalPercentile : null,
        quantitativeScore:
          typeof quantitativeScore === 'number' ? quantitativeScore : null,
        quantitativePercentile:
          typeof quantitativePercentile === 'number'
            ? quantitativePercentile
            : null,
        analyticalWritingScore:
          typeof analyticalWritingScore === 'number'
            ? analyticalWritingScore
            : null,
        analyticalWritingPercentile:
          typeof analyticalWritingPercentile === 'number'
            ? analyticalWritingPercentile
            : null,
        verbalScore: typeof verbalScore === 'number' ? verbalScore : null,
        verbalPercentile:
          typeof verbalPercentile === 'number' ? verbalPercentile : null,
        integratedReasoningScore:
          typeof integratedReasoningScore === 'number'
            ? integratedReasoningScore
            : null,
        integratedReasoningPercentile:
          typeof integratedReasoningPercentile === 'number'
            ? integratedReasoningPercentile
            : null,
        dataInsightsScore:
          typeof dataInsightsScore === 'number' ? dataInsightsScore : null,
        dataInsightsPercentile:
          typeof dataInsightsPercentile === 'number'
            ? dataInsightsPercentile
            : null,
        resultReceiptDate,
      },
      expectedResultUpdateDate:
        expectedResultUpdateDate !== '' ? expectedResultUpdateDate : null,
      supportingDocuments: inputSupportingDocuments,
      ...others,
    };
    await handleLoading(async () => {
      try {
        if (!otherTestId) {
          const res = await createOtherTest({
            variables: {
              id: applicationId,
              input: { otherTest },
            },
          });
          const resOtherTestId = res.data?.addApplicationItem.otherTests[0]._id;
          setOtherTestId(resOtherTestId);
        } else {
          await updateOtherTest({
            variables: {
              id: applicationId,
              updateId: otherTestId,
              input: {
                otherTest,
              },
            },
          });
        }
        refetchApplication();
        alertDetailDef = {
          alertContent: isConditionEditable // if update through the condition meet
            ? PAGE_TEXT.CONDITION_SAVE_ALTER_TEXT
            : 'Saved Other Test Successfully.',
          alertType: 'success',
        };
      } catch (error) {
        alertDetailDef = {
          alertContent: `Saved Other Test Failed.` + (error as Error).message,
          alertType: 'error',
        };
      } finally {
        setAlertDetails(alertDetailDef);
      }
    });

    setOpenAlert(true);
  };

  const formik = useFormik<OtherTestFormValues>({
    initialValues: {
      ...others,
    },
    validationSchema: object({
      testType: string().required('Required'),
      testDate: date().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          const required: boolean = resultAnnouncedIndicator as boolean;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      resultReceiptDate: date().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          const required: boolean = resultAnnouncedIndicator as boolean;
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),

      // GMA(online)1 - 8
      // GMA 1 - 10
      // GRE 1 - 4 , 9,10
      // 1 - 4 for each situation when announced
      verbalScore: number().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          if (resultAnnouncedIndicator === false) {
            return schema.notRequired();
          }
          return schema.required('Required').test({
            message: 'Out of range',
            test: function (value) {
              switch (this.parent.testType) {
                // verbalScore in GRE and GMAT must be integer
                case OTHER_TEST_TYPE.GRE:
                  return (
                    Number.isInteger(value) &&
                    value >= Number(OTHER_TEST.SCORE_RANGE.GRE.VERBALMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GRE.VERBALMAX)
                  );
                case OTHER_TEST_TYPE.GMAT:
                case OTHER_TEST_TYPE.GMAT_ONLINE:
                  return (
                    Number.isInteger(value) &&
                    value >= Number(OTHER_TEST.SCORE_RANGE.GMAT.VERBALMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GMAT.VERBALMAX)
                  );
                case OTHER_TEST_TYPE.GMAT_FOCUS:
                  return (
                    Number.isInteger(value) &&
                    value >=
                      Number(OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.VERBALMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.VERBALMAX)
                  );
                default:
                  return false;
              }
            },
          });
        },
      ),
      verbalPercentile: number().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          if (resultAnnouncedIndicator === false) {
            return schema.notRequired();
          }
          return schema
            .required('Required')
            .min(0, 'Invalide number')
            .max(100, 'Out of range');
        },
      ),
      quantitativeScore: number().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          if (resultAnnouncedIndicator === false) {
            return schema.notRequired();
          }
          return schema.required('Required').test({
            message: 'Out of range',
            test: function (value) {
              // quantitativeScore in GRE and GMAT must be integer
              switch (this.parent.testType) {
                case OTHER_TEST_TYPE.GRE:
                  return (
                    Number.isInteger(value) &&
                    value >=
                      Number(OTHER_TEST.SCORE_RANGE.GRE.QUANTITATIVEMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GRE.QUANTITATIVEMAX)
                  );
                case OTHER_TEST_TYPE.GMAT:
                case OTHER_TEST_TYPE.GMAT_ONLINE:
                  return (
                    Number.isInteger(value) &&
                    value >=
                      Number(OTHER_TEST.SCORE_RANGE.GMAT.QUANTITATIVEMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GMAT.QUANTITATIVEMAX)
                  );
                case OTHER_TEST_TYPE.GMAT_FOCUS:
                  return (
                    Number.isInteger(value) &&
                    value >=
                      Number(
                        OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.QUANTITATIVEMIN,
                      ) &&
                    value <=
                      Number(OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.QUANTITATIVEMAX)
                  );
                default:
                  return false;
              }
            },
          });
        },
      ),
      quantitativePercentile: number().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          if (resultAnnouncedIndicator === false) {
            return schema.notRequired();
          }
          return schema
            .required('Required')
            .min(0, 'Invalide number')
            .max(100, 'Out of range');
        },
      ),
      totalScore: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (resultAnnouncedIndicator === true) {
            switch (testType) {
              case OTHER_TEST_TYPE.GMAT:
              case OTHER_TEST_TYPE.GMAT_ONLINE:
                return schema
                  .required('Required')
                  .min(
                    Number(OTHER_TEST.SCORE_RANGE.GMAT.TOTALMIN),
                    'Out of range',
                  )
                  .max(
                    Number(OTHER_TEST.SCORE_RANGE.GMAT.TOTALMAX),
                    'Out of range',
                  );
              case OTHER_TEST_TYPE.GMAT_FOCUS:
                return schema
                  .required('Required')
                  .min(
                    Number(OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.TOTALMIN),
                    'Out of range',
                  )
                  .max(
                    Number(OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.TOTALMAX),
                    'Out of range',
                  );
              default:
                schema.notRequired();
            }
          }
          return schema.notRequired();
        },
      ),
      totalPercentile: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType !== OTHER_TEST_TYPE.GRE
          ) {
            return schema
              .required('Required')
              .min(0, 'Invalide number')
              .max(100, 'Out of range');
          }
          return schema.notRequired();
        },
      ),
      integratedReasoningScore: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType !== OTHER_TEST_TYPE.GRE &&
            testType !== OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema
              .required('Required')
              .min(
                Number(OTHER_TEST.SCORE_RANGE.GMAT.INTEGRATEDMIN),
                'Out of range',
              )
              .max(
                Number(OTHER_TEST.SCORE_RANGE.GMAT.INTEGRATEDMAX),
                'Out of range',
              );
          }
          return schema.notRequired();
        },
      ),
      integratedReasoningPercentile: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType !== OTHER_TEST_TYPE.GRE &&
            testType !== OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema
              .required('Required')
              .min(0, 'Invalide number')
              .max(100, 'Out of range');
          }

          return schema.notRequired();
        },
      ),

      analyticalWritingScore: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType !== OTHER_TEST_TYPE.GMAT_ONLINE &&
            testType !== OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema.required('Required').test({
              message: 'Out of range',
              test: function (value) {
                if (
                  (this.parent as OtherTestFormValues)?.testType ===
                  OTHER_TEST_TYPE.GRE
                ) {
                  // Analytical Writing in GRE may not be integer
                  return (
                    value >= Number(OTHER_TEST.SCORE_RANGE.GRE.ANALYTICALMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GRE.ANALYTICALMAX)
                  );
                } else {
                  return (
                    // Analytical Writing in GMAT may not be integer
                    value >=
                      Number(OTHER_TEST.SCORE_RANGE.GMAT.ANALYTICALMIN) &&
                    value <= Number(OTHER_TEST.SCORE_RANGE.GMAT.ANALYTICALMAX)
                  );
                }
              },
            });
          }
          return schema.notRequired();
        },
      ),

      analyticalWritingPercentile: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType !== OTHER_TEST_TYPE.GMAT_ONLINE &&
            testType !== OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema
              .required('Required')
              .min(0, 'Invalide number')
              .max(100, 'Out of range');
          }
          return schema.notRequired();
        },
      ),

      dataInsightsScore: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType === OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema
              .required('Required')
              .min(60, 'Out of range')
              .max(90, 'Out of range');
          }
          return schema.notRequired();
        },
      ),

      dataInsightsPercentile: number().when(
        ['resultAnnouncedIndicator', 'testType'],
        ([resultAnnouncedIndicator, testType], schema) => {
          if (
            resultAnnouncedIndicator === true &&
            testType === OTHER_TEST_TYPE.GMAT_FOCUS
          ) {
            return schema
              .required('Required')
              .min(0, 'Invalide number')
              .max(100, 'Out of range');
          }
          return schema.notRequired();
        },
      ),
      expectedResultUpdateDate: date().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator], schema) => {
          const required: boolean = !(resultAnnouncedIndicator as boolean);
          return required ? schema.required('Required') : schema.notRequired();
        },
      ),
      referenceNo: string().when(
        'resultAnnouncedIndicator',
        ([resultAnnouncedIndicator]: boolean[], schema) => {
          return resultAnnouncedIndicator
            ? schema.required('Required')
            : schema.notRequired();
        },
      ),
    }),
    onSubmit: handleSubmit,
  });

  const otherTestPermission = editOtherTestPermission(
    initialValues,
    {
      ...formik.values,
      supportingDocuments: files,
    },
    enableEdit,
  );

  const handleResultAnnouncedIndicatorChange = async (
    event: React.SyntheticEvent<Element, Event>,
    checked: boolean,
  ): Promise<void> => {
    await removeResult();
    await formik.setFieldValue('testDate', null);
    await formik.setFieldValue('expectedResultUpdateDate', null);
    await formik.setFieldValue('resultAnnouncedIndicator', checked);
  };

  const handleTestTypeChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    const currTestType = formik.values.testType;
    const newValue = event.target.value;
    if (newValue !== currTestType) {
      await removeResult();
    }
    await formik.setFieldValue('testType', newValue);
  };

  const handleFilesChange = (order: number, id: string): boolean => {
    let newSupportingDocuments = [...supportingDocuments];
    switch (order) {
      case FILE_MANAGE_ORDER.REMOVE:
        newSupportingDocuments = newSupportingDocuments.filter(
          file => file.id !== id,
        );
        break;
      case FILE_MANAGE_ORDER.UPLOAD:
        newSupportingDocuments = [
          ...newSupportingDocuments,
          {
            id,
            name: '',
            link: '',
          },
        ];
        break;
      default:
        break;
    }
    setFiles(newSupportingDocuments);
    return true;
  };

  const removeResult = async (): Promise<void> => {
    const currValues = formik.values;
    await formik.setValues({
      ...currValues,
      testDate: null,
      resultReceiptDate: null,
      expectedResultUpdateDate: null,
      totalScore: '',
      totalPercentile: '',
      quantitativePercentile: '',
      quantitativeScore: '',
      analyticalWritingScore: '',
      analyticalWritingPercentile: '',
      verbalScore: '',
      verbalPercentile: '',
      integratedReasoningScore: '',
      integratedReasoningPercentile: '',
      dataInsightsPercentile: '',
      dataInsightsScore: '',
      referenceNo: '',
    });
  };

  const isResultAnnounced = formik.values.resultAnnouncedIndicator;
  const isGRE = formik.values.testType === OTHER_TEST_TYPE.GRE;
  const isGMATFocus = formik.values.testType === OTHER_TEST_TYPE.GMAT_FOCUS;
  const isGMAT = formik.values.testType === OTHER_TEST_TYPE.GMAT;
  const isOnlineGMAT = formik.values.testType === OTHER_TEST_TYPE.GMAT_ONLINE;
  const scoreRangeText = isGRE
    ? OTHER_TEST.SCORE_RANGE.GRE
    : OTHER_TEST.SCORE_RANGE.GMAT;

  const resultFields: ResultFields[] = [
    // Total: GMAT, GMAT Online, GMAT Focus
    {
      label: OTHER_TEST.TOTAL,
      display: isGMAT || isOnlineGMAT || isGMATFocus,
      children: [
        {
          name: 'totalScore',
          label: `${OTHER_TEST.SCORE} (${
            isGMATFocus
              ? OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.TOTAL
              : OTHER_TEST.SCORE_RANGE.GMAT.TOTAL
          })`,
          min: isGMATFocus ? 205 : 200,
          max: isGMATFocus ? 805 : 800,
        },
        {
          name: 'totalPercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
    {
      label: isGMATFocus ? OTHER_TEST.VERBAL_REASONING : OTHER_TEST.VERBAL,
      display: true,
      children: [
        {
          name: 'verbalScore',
          label: `${OTHER_TEST.SCORE} (${
            isGMATFocus
              ? OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.VERBAL
              : scoreRangeText.VERBAL
          })`,
          min: isGRE ? 130 : isGMATFocus ? 60 : 0,
          max: isGRE ? 170 : isGMATFocus ? 90 : 60,
        },
        {
          name: 'verbalPercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
    {
      label: isGMATFocus
        ? OTHER_TEST.QUANTITATIVE_REASONING
        : OTHER_TEST.QUANTITATIVE,
      display: true,
      children: [
        {
          name: 'quantitativeScore',
          label: `${OTHER_TEST.SCORE} (${
            isGMATFocus
              ? OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.QUANTITATIVE
              : scoreRangeText.QUANTITATIVE
          })`,
          min: isGRE ? 130 : isGMATFocus ? 60 : 0,
          max: isGRE ? 170 : isGMATFocus ? 90 : 60,
        },
        {
          name: 'quantitativePercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
    // Integrated Reasoning: GMAT, GMAT Online
    {
      label: OTHER_TEST.INTEGRATED_REASONING,
      display: isGMAT || isOnlineGMAT,
      children: [
        {
          name: 'integratedReasoningScore',
          label: `${OTHER_TEST.SCORE} (${OTHER_TEST.SCORE_RANGE.GMAT.INTEGRATED})`,
          min: 0,
          max: 8,
        },
        {
          name: 'integratedReasoningPercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
    // Analytical Writing: GRE, GMAT
    {
      label: OTHER_TEST.ANALYTICAL_WRITING,
      display: isGRE || isGMAT,
      children: [
        {
          name: 'analyticalWritingScore',
          label: `${OTHER_TEST.SCORE} (${scoreRangeText.ANALYTICAL})`,
          min: 0,
          max: 8,
        },
        {
          name: 'analyticalWritingPercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
    // Data Insights: GMAT Focus
    {
      label: OTHER_TEST.DATA_INSIGHTS,
      display: isGMATFocus,
      children: [
        {
          name: 'dataInsightsScore',
          label: `${OTHER_TEST.SCORE} (${OTHER_TEST.SCORE_RANGE.GMAT_FOCUS.DATA_INSIGHTS})`,
          min: 60,
          max: 90,
        },
        {
          name: 'dataInsightsPercentile',
          label: OTHER_TEST.PERCENTILE,
          min: 0,
          max: 100,
        },
      ],
    },
  ];
  const handleCancelSubmit = (): void => {
    setOpenDialog(false);
  };
  const handleOpenDialog = (): void => {
    setOpenDialog(true);
  };
  const handleNavToMeetCondition = (): void => {
    window.location.href =
      '/application/' +
      applicationId +
      '?apsb=' +
      APPLICATION_MODULE.APPLICATION_PROGRESS +
      '&aphb=' +
      APPLICATION_STATUS_MODULE.CONDITIONAL_OFFER_CONFIRMATION;
  };

  return (
    <Container>
      <Dialog
        openDialog={openDialog}
        dialogTitle={''}
        dialogAction={PAGE_TEXT.GO_SUBMIT}
        cancelAction={PAGE_TEXT.BACK_TO_CHECK}
        maxWidth={'sm'}
        onDialogAction={handleNavToMeetCondition}
        onDialogClose={handleCancelSubmit}
      >
        <Typography margin={'0rem 1.5rem 2rem'}>
          {PAGE_TEXT.QUALIFICATION.DIALOG_NOTE}
        </Typography>
      </Dialog>
      <Alert
        openAlert={openAlert}
        setOpenAlert={setOpenAlert}
        alertDetails={alertDetails}
      />
      <form
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        onSubmit={handleReadySubmit(formik)}
        // prevent browser validate, otherwise will block formik setTouched when submitting
        noValidate
      >
        <div className="flex flex-row w-full justify-between">
          <div className="w-full">
            <Grid container columnSpacing={4} rowSpacing={2}>
              <Grid
                size={{
                  md: 12,
                }}
              >
                <Select
                  className="w-full"
                  options={otherTestTypeOptions}
                  disabled={!otherTestPermission.testType}
                  label={OTHER_TEST.TEST_TYPE}
                  id="other-test-test-type"
                  name="testType"
                  required
                  InputLabelProps={{ shrink: true }}
                  value={formik.values.testType}
                  onChange={handleTestTypeChange}
                  onBlur={formik.handleBlur}
                  error={
                    Boolean(formik.touched.testType) &&
                    Boolean(formik.errors.testType)
                  }
                  helperText={Boolean(
                    formik.touched.testType && formik.errors.testType,
                  )}
                />
              </Grid>
              <Grid
                size={{
                  md: 12,
                }}
              >
                <span>
                  <SwitchButton
                    checked={Boolean(formik.values.resultAnnouncedIndicator)}
                    disabled={!otherTestPermission.resultAnnouncedIndicator}
                    value={Boolean(formik.values.resultAnnouncedIndicator)}
                    onChange={handleResultAnnouncedIndicatorChange}
                  />
                  {OTHER_TEST.TEST_RESULT_ANNOUNCED}
                </span>
              </Grid>
              {isResultAnnounced ? (
                <>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      className="w-full"
                      disabled={!otherTestPermission.testDate}
                      label={OTHER_TEST.TEST_DATE}
                      id="other-test-test-date"
                      name="testDate"
                      required
                      InputLabelProps={{ shrink: true }}
                      maxDate={dayjs()}
                      value={
                        formik.values.testDate !== null
                          ? dayjs(formik.values.testDate)
                          : null
                      }
                      onChange={date => {
                        void formik.setFieldValue('testDate', date);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        Boolean(formik.touched.testDate) &&
                        Boolean(formik.errors.testDate)
                      }
                      helperText={
                        Boolean(formik.touched.testDate) &&
                        formik.errors.testDate
                      }
                    />
                  </Grid>
                  <Grid
                    size={{
                      xs: 12,
                      md: 6,
                    }}
                  >
                    <DatePicker
                      className="w-full"
                      disabled={!otherTestPermission.resultReceiptDate}
                      label={OTHER_TEST.RESULT_RECEIPT_DATE}
                      id="other-test-date"
                      name="resultReceiptDate"
                      required
                      InputLabelProps={{ shrink: true }}
                      maxDate={dayjs()}
                      minDate={dayjs(formik.values.testDate)}
                      value={
                        formik.values.resultReceiptDate !== null
                          ? dayjs(formik.values.resultReceiptDate)
                          : null
                      }
                      onChange={date => {
                        void formik.setFieldValue('resultReceiptDate', date);
                      }}
                      onBlur={formik.handleBlur}
                      error={
                        Boolean(formik.touched.resultReceiptDate) &&
                        Boolean(formik.errors.resultReceiptDate)
                      }
                      helperText={
                        Boolean(formik.touched.resultReceiptDate) &&
                        formik.errors.resultReceiptDate
                      }
                    />
                  </Grid>
                  {resultFields.map((item, index) => {
                    const { label, display, children } = item;
                    return (
                      display && (
                        <Grid
                          key={index}
                          size={{
                            xs: 12,
                            md: 6,
                          }}
                        >
                          <h3>{label}</h3>
                          <div>
                            {children.map((child, childIndex) => {
                              const { name, label, max, min } = child;
                              return (
                                <TextField
                                  key={childIndex}
                                  style={{
                                    minWidth: '49%',
                                    marginRight: `${childIndex ? '0' : '2%'}`,
                                  }}
                                  disabled={
                                    !otherTestPermission[
                                      name as keyof OtherTestFieldPermissions
                                    ]
                                  }
                                  label={label}
                                  name={name}
                                  type="number"
                                  required
                                  InputLabelProps={{ shrink: true }}
                                  value={formik.values[name]}
                                  onChange={formik.handleChange}
                                  onBlur={formik.handleBlur}
                                  inputProps={{
                                    min,
                                    max,
                                    step: 1,
                                  }}
                                  error={
                                    formik.touched[name] &&
                                    Boolean(formik.errors[name])
                                  }
                                  helperText={
                                    formik.touched[name] && formik.errors[name]
                                  }
                                />
                              );
                            })}
                          </div>
                        </Grid>
                      )
                    );
                  })}
                  <Grid
                    size={{
                      xs: 12,
                      md: 12,
                    }}
                  >
                    <TextField
                      className="w-full"
                      label={OTHER_TEST.REFERENCE_NO}
                      disabled={!otherTestPermission.referenceNo}
                      InputLabelProps={{ shrink: true }}
                      required
                      name={'referenceNo'}
                      id="other-test-reference-no"
                      value={formik.values.referenceNo}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.referenceNo &&
                        Boolean(formik.errors.referenceNo)
                      }
                      helperText={
                        formik.touched.referenceNo && formik.errors.referenceNo
                      }
                    />
                  </Grid>
                </>
              ) : (
                <Grid
                  size={{
                    xs: 12,
                    md: 6,
                  }}
                >
                  <DatePicker
                    className="w-full"
                    label={OTHER_TEST.I_WILL_UPDATE_RESULT_AT}
                    disabled={!otherTestPermission.expectedResultUpdateDate}
                    id="other-test-expected-result-update-date"
                    name="expectedResultUpdateDate"
                    required
                    InputLabelProps={{ shrink: true }}
                    minDate={dayjs()}
                    value={
                      formik.values.expectedResultUpdateDate !== null
                        ? dayjs(formik.values.expectedResultUpdateDate)
                        : null
                    }
                    onChange={date => {
                      void formik.setFieldValue(
                        'expectedResultUpdateDate',
                        date,
                      );
                    }}
                    onBlur={formik.handleBlur}
                    error={
                      formik.touched.expectedResultUpdateDate &&
                      Boolean(formik.errors.expectedResultUpdateDate)
                    }
                    helperText={
                      formik.touched.expectedResultUpdateDate &&
                      formik.errors.expectedResultUpdateDate
                    }
                  />
                </Grid>
              )}
            </Grid>
            {!isResultAnnounced && (
              <div
                style={{
                  marginLeft: '1rem',
                  fontStyle: 'italic',
                  color: 'red',
                }}
              >
                {PAGE_TEXT.QUALIFICATION.OTHER_TEST.HELPER_TEXT_NOTE}
              </div>
            )}
          </div>
          <div style={{ paddingTop: '15px', marginLeft: '1rem' }}>
            <FilesPanel
              disable={!otherTestPermission.supportingDocuments}
              disableDeleteOnly={!enableDelete}
              title={PAGE_TEXT.FILES_UPLOAD}
              caption={`${PAGE_TEXT.SUPPORTING_DOCUMENTS}`}
              files={files}
              handleFilesChange={handleFilesChange}
              handleLoading={handleLoading}
            />
          </div>
        </div>

        <Box display={'flex'} justifyContent={'center'} marginTop={6}>
          {otherTestPermission.enableSave && (
            <Button
              type="submit"
              variant="contained"
              style={{ marginRight: '15px' }}
            >
              {BUTTON.SAVE}
            </Button>
          )}
          {otherTestPermission.enableSaveCondition && (
            <Button
              variant="contained"
              onClick={handleOpenDialog}
              sx={{ marginLeft: '2rem' }}
            >
              {PAGE_TEXT.QUALIFICATION.CONDITION_MEET}
            </Button>
          )}
          {otherTestPermission.enableDelete && enableDelete && (
            <Button variant="outlined" onClick={handleDelete}>
              {BUTTON.DELETE}
            </Button>
          )}
        </Box>
      </form>
    </Container>
  );
};

const Container = styled.div`
  margin-top: 35px;
  flex: 1;
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
  width: 100%;

  .MuiSwitch-root {
    margin: 1rem;
  }

  form {
    margin-bottom: 2rem;
    width: 100%;
    padding-top: 0.5rem;
  }

  .MuiGrid-root .MuiGrid-item {
    margin-bottom: 0.5rem;
    display: flex;
    flex-direction: column;
    padding: 0.5rem 1rem;
  }

  .MuiInputBase-root {
    margin: 0 !important;
  }

  .MuiGrid-container {
    margin-top: 0;
    margin-left: 0;

    width: calc(100%);
  }

  h3 {
    margin-bottom: 0.5rem;
  }
`;
